/* eslint-disable no-unused-vars */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getInvoices } from "../api/ApiRoutesFile";

export const invoiceApiSlice = createApi({
  reducerPath: "invoiceApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Invoices"],
  endpoints: (builder) => ({
    invoices: builder.query({
      query: ({ endpoint, id, params, search }) => {
        console.log('Invoice API query params:', { endpoint, id, params, search });
        
        let url = id ? `${endpoint}${id}` : endpoint;
        
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        // Add existing params if they exist
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              queryParams.append(key, value);
            }
          });
        }
        
        // Add search parameter if provided
        if (search && search.trim() !== '') {
          queryParams.append('search', search.trim());
          console.log('Search parameter added to invoice query:', search.trim());
        }
        
        // Append query string if we have parameters
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
        
        console.log('Final invoice API URL:', url);
        
        return {
          url,
          method: "GET",
        };
      },
      // Disable caching for search results to ensure fresh data
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      // Add a more specific cache key including search
      serializeQueryArgs: ({ queryArgs }) => {
        const { endpoint, id, params, search } = queryArgs;
        // Create a unique cache key based on all parameters including search
        return `${endpoint}${id || ""}${JSON.stringify(params) || ""}${search || ""}`;
      },
      // Improve the tag structure to be more specific
      providesTags: (result, error, { params, search }) => {
        // Add params and search to the LIST tag to make it unique per query
        const listTag = { type: "Invoices", id: `LIST${JSON.stringify(params) || ""}${search || ""}` };
        
        // Add search-specific tags for better cache invalidation
        const tags = [listTag];
        
        if (search && search.trim()) {
          tags.push({ type: "Invoices", id: `SEARCH-${search.trim()}` });
        }

        if (!result || !result.transactions) {
          return tags;
        }

        return [
          ...result.transactions.map(({ _id }) => ({
            type: "Invoices",
            id: _id,
          })),
          ...tags,
        ];
      },
    }),

    updateInvoice: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      // Invalidate tags more specifically
      invalidatesTags: (result, error, { id, params, search }) => [
        { type: "Invoices", id },
        { type: "Invoices", id: `LIST${JSON.stringify(params) || ""}${search || ""}` },
        ...(search && search.trim() ? [{ type: "Invoices", id: `SEARCH-${search.trim()}` }] : []),
      ],
      // The optimistic update approach
      async onQueryStarted(
        { endpoint, id, formData, params },
        { dispatch, queryFulfilled }
      ) {
        try {
          // Get the API query args to match the cache key
          const patchResult = dispatch(
            invoiceApiSlice.util.updateQueryData(
              "invoices",
              { endpoint: getInvoices, id: params?.lastId || "", params },
              (draft) => {
                const index = draft.transactions.findIndex((a) => a._id === id);
                if (index !== -1) {
                  // Apply optimistic update
                  draft.transactions[index] = {
                    ...draft.transactions[index],
                    ...formData,
                  };
                }
              }
            )
          );

          // Wait for the actual API response
          const { data } = await queryFulfilled;

          // If API call succeeded, you can update with real data
          if (data && data.transaction) {
            dispatch(
              invoiceApiSlice.util.updateQueryData(
                "invoices",
                { endpoint: getInvoices, id: params?.lastId || "", params },
                (draft) => {
                  const index = draft.transactions.findIndex(
                    (a) => a._id === id
                  );
                  if (index !== -1) {
                    draft.transactions[index] = data.transaction;
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Update failed:", error);
          // Handle rollback if needed
        }
      },
    }),
  }),
});

export const { useInvoicesQuery, useUpdateInvoiceMutation } = invoiceApiSlice;
